<template>
	<div class="counter-container">
		<div class="counter-menu-container">
			<div class="macro-filters-container">
				<div class="first-row">
					<div class="menus-scroll-container" v-if="menuSelected && sortedMenus.length > 0">
						<MenusFilter ref="menus-filter" :menus="sortedMenus" :selected-menu="selectedMenu ? Number(selectedMenu.menu_id) : null" @select-menu="(menuId) => changeSelectedMenu(menuId)" />
					</div>
					<div class="menus-empty-container" :class="{noHeight: !menuSelected}" v-else></div>
					<div class="left-search-container">
						<div class="search-container" v-if="menuSelected">
							<AppClickableIcon class="search-icon" icon-name="iconX" v-if="!!counterStore.search" @click-icon="endSearch" />
							<AppTextInput v-if="expandedSearch" :class="{searching: counterStore.search ? counterStore.search.length > 0 : false}" ref="search-input" icon-name="icon-magnify" :value="counterStore.search" :placeholder="getString('search')" @changed-text="changeSearch" />
							<div v-else class="order-switch order-switch-search" :class="{selected: showSearch, searching: counterStore.search ? counterStore.search.length > 0 : false}" @click="toggleSearch">
								<img :src="'https://img.qromo.io/img/icons/icon-search-primary' + (this.userStore.darkMode ? '-dark' : '') + '.svg'" alt="">
								<AppInPlaceCloud :active="showSearch">
									<AppTextInput v-click-out-side="closeSearch" ref="search-input-modal" :value="counterStore.search" :placeholder="getString('search')" @changed-text="changeSearch" />
								</AppInPlaceCloud>
							</div>
						</div>
						<div v-if="showNumpadButton" class="order-switch ml-8" @click="toggleNumpad">
							<img :src="iconSwitchNumpad" alt="">
						</div>
					</div>
				</div>
				<div class="second-row" v-if="menuSelected">
					<CategoriesFilter ref="categories-filter" :selected-categories="filterableCategories" @select-category="(catId) => activeCategory = catId" />
				</div>
			</div>
			<div class="categories-container" v-if="menuSelected">
				<RecycleScroller
					class="all-categories-container"
					:class="{appearing: !noCategories}"
					:items="virtualRows"
					:item-size="null"
					size-field="size"
					key-field="id"
					v-slot="{ item: row }">
					<CounterCategoryHeader
						v-if="row.type === 'header'"
						:category="row.category"
						:is-virtual="row.isVirtual" />
					<div v-else class="category-items-row" :class="{ 'is-virtual': row.isVirtual, 'is-last': row.isLast }">
						<CounterCategoryItem
							v-for="it in row.items"
							:key="it.menu_item_id"
							:item="it"
							:taxonomy="row.taxonomy"
							:fallback-color="row.color" />
					</div>
				</RecycleScroller>
				<div class="no-categories-container" v-if="noCategoriesDelayed" :class="{appearing: noCategories}">
					<div class="no-categories" v-if="menuStore.loadedBusinessMenu">
						<div class="no-categories-text">{{ getString('no_items') }}</div>
					</div>
					<div class="no-categories" v-else>
						<AppLoader />
					</div>
				</div>
			</div>
			<div v-if="menuSelected" class="floating-buttons-container">
				<div v-if="shortcutIconUrl !== null" class="floating-button shortcut" :class="{'disabled': shortcutDisabled}" @click="performShortcut">
					<img :src="shortcutIconUrl" alt="">
				</div>
				<div v-if="userStore.currBusiness && userStore.currBusiness.counter_courses" class="floating-button courses" @click="clickMobileCtaCourses">
					<p>{{ counterStore.selectedCourse }}</p>
				</div>
				<div class="floating-button" @click="clickMobileCta">
					<img :src="isMobileCtaOrder ? 'https://img.qromo.io/img/icons/icon-order-summary.svg' : 'https://img.qromo.io/img/icons/icon-table-white.svg'" alt="">
				</div>
			</div>
			<CounterNumpad v-else />
		</div>
		<div class="counter-order-container" :class="{transition: ordersStore.transitioningMobileOrderSummary, active: ordersStore.showMobileOrderSummary}" @click.self="ordersStore.deactivateMobileOrderSummary()">
			<CounterOrder ref="counterOrder" :active-menu-id="selectedMenu?.menu_id || null" />
		</div>
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="!!counterStore.currComposite" @toggling-popup="toggleBottomSheet" >
			<div class="composite-add-container" v-if="!!counterStore.currComposite">
				<ModalHeader :title="counterStore.currComposite ? counterStore.currComposite.name : ''"/>
				<AppLoader v-if="counterStore.loadingComposite" class="composite-loader" />
				<div class="composite-container mb-60" v-if="!counterStore.loadingComposite">
					<div class="lists-container">
						<CompositeList :list="list" v-for="list in compositeLists" :key="list.menu_item_list_id"/>
					</div>
				</div>
				<div class="wrapper-fixed-button">
					<AppButton :disabled="disabledAddButton" icon-left="icon-plus" outline :text="getString('add')" @click="counterStore.addCompositeItem" />
				</div>
			</div>
		</AppBottomSheet>
		<TablesSelect :active="counterStore.tablesActive" @toggling-popup="togglingTables" @selected-table="selectedTable" />
	</div>
</template>

<script>
import CounterOrder from "@/console-new/components/ops/counter/CounterOrder.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import {useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import {useMenuStore, useOrdersStore, useUserStore, useWarehouseStore} from "@/console-new/stores";
import CategoriesFilter from "@/console-new/components/ops/counter/CategoriesFilter.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AvailabilityDot from "@/console-new/components/config/menu/AvailabilityDot.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import CompositeList from "@/console-new/components/ops/counter/CompositeList.vue";
import {useCounterStore} from "@/console-new/stores/counter.store";
import CounterCategoryHeader from "@/console-new/components/ops/counter/CounterCategoryHeader.vue";
import CounterCategoryItem from "@/console-new/components/ops/counter/CounterCategoryItem.vue";
import { RecycleScroller } from "vue-virtual-scroller";
import BottomNumberEditor from "@/console-new/components/ops/counter/BottomNumberEditor.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import BottomNumberEditorNumpad from "@/console-new/components/ops/counter/BottomNumberEditorNumpad.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import CounterNumpad from "@/console-new/components/ops/counter/CounterNumpad.vue";
import TablesSelect from "@/console-new/components/ops/counter/TablesSelect.vue";
import AppInPlaceCloud from "@/shared/components/AppInPlaceCloud.vue";
import clickOutSide from "@mahdikhashan/vue3-click-outside";
import AppButton from "@/shared/components/AppButton.vue";
import MenusFilter from "@/console-new/components/ops/counter/MenusFilter.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let menuStore = useMenuStore();
		let notificationStore = useNotificationStore();
		let counterStore = useCounterStore();
		let ordersStore = useOrdersStore();
		let svgStore = useSvgStore();
		const warehouseStore = useWarehouseStore();
		return {languageStore, getString: languageStore.getString, userStore, warehouseStore, menuStore, notificationStore, counterStore, ordersStore, svgStore};
	},
	directives: {
		clickOutSide,
	},
	name: "CounterSalesContainer",
	mixins: [WindowWidth],
	components: {
		MenusFilter,
		AppButton,
		AppInPlaceCloud,
		TablesSelect,
		CounterNumpad,
		AppButtonIcon,
		BottomNumberEditorNumpad,
		AppSwitch,
		CounterCategoryHeader,
		CounterCategoryItem,
		RecycleScroller,
		CompositeList,
		ModalHeader, AppBottomSheet, AvailabilityDot, AppLoader, AppTextInput, AppSelect, CounterOrder, AppClickableIcon, CategoriesFilter, BottomNumberEditor},
	data() {
		return {
			menuIndex: null,
			activeCategory: null,
			menuSelected: this.getDefaultSwitchPosition(),
			numpadNumber: 0,
			showSearch: false,
			noCategoriesDelayed: true,
		}
	},
	computed: {
		noCategories() {
			return this.selectedMenuCategories.length === 0;
		},
		expandedSearch() {
			return this.windowWidth > 1300;
		},
		isMobileCtaOrder() {
			if(
				!["basic", "free"].includes(this.userStore.currSubscription)
				&& this.counterStore.selectedMode === 'counter'
				&& this.userStore.currBusiness?.counter_mandatory_table
				&& (
					(!!this.counterStore.editOrder && !this.counterStore.editOrder?.room_table_id)
					|| (!this.counterStore.editOrder && !this.counterStore.selectedTable)
				)
			) {
				return false;
			}
			return this.counterStore.cartItemsCount > 0 || !!this.counterStore.selectedTable || ["basic", "free"].includes(this.userStore.currSubscription);
		},
		sortedMenus() {
			return this.menuStore.sortedMenus.filter(menu => menu.counter_available === true);
		},
		selectedMenu() {
			return this.menuIndex !== null ? this.sortedMenus[this.menuIndex] : null;
		},
		filterableCategories() {
			if (!this.selectedMenu?.categories) return [];

			const search = this.counterStore.search?.toLowerCase() || '';

			return this.selectedMenu.sortedCategories
				.filter(c => c && !c.variations_only && !c.additional_costs_shortcuts)
				.map(c => this.filterCategoryItems(c, search))
				.filter(c => c.sortedItems.length > 0);
		},
		selectedMenuCategories() {
			if (!this.selectedMenu?.categories) return [];

			const search = this.counterStore.search?.toLowerCase() || '';

			if (this.activeCategory) {
				const category = this.selectedMenu.categories[this.activeCategory];
				if (!category) return [];

				return [this.filterCategoryItems(category, search)];
			}

			return this.filterableCategories;
		},
		itemsPerRow() {
			const w = this.windowWidth;
			if (w <= 360) return 1;
			if (w <= 600) return 2;
			if (w <= 750) return 3;
			if (w <= 990) return 2;
			if (w <= 1220) return 3;
			if (w <= 1490) return 4;
			if (w <= 1695) return 5;
			if (w <= 1895) return 6;
			if (w <= 2100) return 7;
			return 8;
		},
		virtualRows() {
			const rows = [];
			const N = this.itemsPerRow;

			// HEADER_HEIGHT matches the explicit height on .category-header-row.
			const HEADER_HEIGHT = 48;
			// Item is content-dominated: name-container min-height 44 + margin-bottom 5
			// + info-adv ~15 + outer padding 12 = 76 (constant across breakpoints / image modes).
			const ITEM_HEIGHT = 76;
			const itemRowHeight = ITEM_HEIGHT + (this.windowWidth <= 700 ? 8 : 10);

			const pushCategory = (category, idPrefix, isVirtual) => {
				if (!category?.sortedItems?.length) return;
				rows.push({
					type: 'header',
					id: `${idPrefix}_h`,
					category,
					isVirtual,
					itemsCount: 0,
					size: HEADER_HEIGHT,
				});
				const items = category.sortedItems;
				for (let i = 0; i < items.length; i += N) {
					const chunk = items.slice(i, i + N);
					rows.push({
						type: 'row',
						id: `${idPrefix}_r_${i}`,
						items: chunk,
						itemsCount: chunk.length,
						taxonomy: category.taxonomy,
						color: category.color,
						isVirtual,
						isLast: i + N >= items.length,
						size: itemRowHeight,
					});
				}
			};

			if (this.mostSoldQuantity && this.counterStore.mostSold && this.mostSoldCat.items.length > 0) {
				pushCategory(this.mostSoldCat, 'most_sold', true);
			}

			for (const category of this.selectedMenuCategories) {
				const id = category.menu_category_id ?? category.name;
				pushCategory(category, `cat_${id}`, false);
			}

			return rows;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		compositeLists() {
			if(this.counterStore.currComposite && this.counterStore.currComposite.lists) {
				return Object.values(this.counterStore.currComposite.lists).sort((a, b) => a.order_number - b.order_number);
			} else {
				return [];
			}
		},
		mostSoldQuantity() {
			return this.userStore.currBusiness && this.userStore.currBusiness.most_sold_quantity ? this.userStore.currBusiness.most_sold_quantity : 0;
		},
		mostSoldCat() {
			let result = {
				color: "var(--color-bg-primary-1)",
				name: this.languageStore.getString('most_sold'),
				items: [],
				sortedItems: []
			};

			if (!this.mostSoldQuantity || !this.counterStore.mostSold || this.activeCategory) {
				return result;
			}

			let totalCount = 0;
			let filteredItems = [];

			for (let i = 0; i < this.selectedMenuCategories.length; i++) {
				let cat = this.selectedMenuCategories[i];
				let catItems = Object.values(cat.items);
				catItems = catItems.filter(it => !it.deleted_item);
				totalCount += catItems.length;

				for (let j = 0; j < catItems.length; j++) {
					let item = catItems[j];
					if (this.counterStore.mostSold.hasOwnProperty(item.menu_item_id) &&
						this.counterStore.mostSold[item.menu_item_id] >= 5) {
						filteredItems.push(item);
					}
				}
			}

			if (totalCount < 15) return result;

			filteredItems.sort((a, b) => {
				return this.counterStore.mostSold[b.menu_item_id] - this.counterStore.mostSold[a.menu_item_id];
			});

			result.items = filteredItems.slice(0, this.mostSoldQuantity);
			result.sortedItems = result.items;
			return result;
		},
		iconSwitchNumpad() {
			if(this.menuSelected) {
				return 'https://img.qromo.io/img/icons/icon-pos-primary' + (this.userStore.darkMode ? '-dark' : '') + '.svg';
			} else {
				if(this.userStore.currBusinessId && this.userStore.currBusiness.type === 'other') {
					return 'https://img.qromo.io/img/icons/icon-menu-full-other-new' + (this.userStore.darkMode ? '-dark' : '') + '.svg';
				} else {
					return 'https://img.qromo.io/img/icons/icon-menu-full' + (this.userStore.darkMode ? '-dark' : '') + '.svg';
				}
			}
		},
		disabledAddButton() {
			if(!this.userStore.currBusiness.counter_variations_limits) {
				return false;
			}
			if(this.counterStore.loadingComposite) {
				return true;
			}
			return this.compositeLists.some(list => {
				if(!list?.select_min || !list?.products || !Object.values(list.products)?.length) {
					return false
				}
				let listProducts = Object.values(list.products);
				let selectedItems = 0;
				for(let product of listProducts) {
					selectedItems += this.counterStore.getProductQuantity(product);
				}
				return selectedItems < list.select_min;
			});
		},
		showNumpadButton() {
			return !this.userStore.currBusiness || this.userStore.currBusiness.show_counter_numpad;
		},
		shortcutIconUrl() {
			if(this.counterStore.editOrder) {
				return null;
			}
			if(this.userStore.currBusiness && this.userStore.currBusiness.counter_shortcut) {
				if(this.userStore.currBusiness.counter_shortcut === "cash") {
					return "https://img.qromo.io/img/icons/icon-money-white.svg";
				} else if(this.userStore.currBusiness.counter_shortcut === "card") {
					return "https://img.qromo.io/img/icons/icon-card-white.svg";
				} else if(this.userStore.currBusiness.counter_shortcut === "paylater") {
					return "https://img.qromo.io/img/icons/icon-paylater-white-2.svg";
				} else {
					return null;
				}
			} else {
				return null;
			}
		},
		shortcutDisabled() {
			return !this.shortcutIconUrl || this.counterStore.cartTotal === 0;
		},
		loadingCta() {
			return false;
		}
	},
	methods: {
		selectedTable() {
			if(this.$refs.counterOrder) {
				this.$refs.counterOrder.selectedTable();
			}
		},
		filterCategoryItems(category, search) {
			if (!search) return category;

			const sortedItems = category.sortedItems.filter(i =>
				i &&
				i.name &&
				!i.deleted_item &&
				!i.variations_only &&
				!i.additional_costs_shortcuts &&
				i.name.toLowerCase().includes(search)
			);

			return { ...category, sortedItems, items: sortedItems };
		},
		togglingTables(isOpen) {
			if(!isOpen) {
				this.counterStore.tablesActive = false;
			}
		},
		clickMobileCta() {
			if(this.isMobileCtaOrder) {
				this.ordersStore.showMobileOrderSummary ? this.ordersStore.deactivateMobileOrderSummary() : this.ordersStore.activateMobileOrderSummary();
			} else {
				this.counterStore.tablesActive = true;
			}
		},
		clickMobileCtaCourses() {
			this.counterStore.selectedCourse += 1;
			if(this.counterStore.selectedCourse > this.counterStore.maxCourse) {
				this.counterStore.maxCourse = this.counterStore.selectedCourse;
			}
		},
		getDefaultSwitchPosition() {
			if(this.userStore.currBusiness && !this.userStore.currBusiness.show_counter_numpad) {
				localStorage.removeItem("counter-sales-switch-position");
				return true;
			}
			let position = localStorage.getItem("counter-sales-switch-position");
			return position !== "numpad";
		},
		toggleBottomSheet(isOpen) {
			if(!isOpen) {
				this.counterStore.currComposite = null;
				this.counterStore.compositeProducts = [];
			}
		},
		endSearch() {
			this.counterStore.search = null;
			if(this.$refs["search-input"]) {
				this.$refs["search-input"].currentValue = "";
			}
			if(this.$refs["search-input-modal"]) {
				this.$refs["search-input-modal"].currentValue = "";
			}
			this.activeCategory = null;
			this.$refs["categories-filter"].activeCategory = null;
		},
		changeSearch(value) {
			this.counterStore.search = value;
			this.activeCategory = null;
			this.$refs["categories-filter"].activeCategory = null;
		},
		changeSelectedMenu(menuId) {
			this.activeCategory = null;
			this.$refs["categories-filter"].activeCategory = null;
			this.menuIndex = this.sortedMenus.findIndex(m => m.menu_id === menuId);
			localStorage.setItem("last_menu_id", menuId);
		},
		toggleNumpad() {
			localStorage.setItem("counter-sales-switch-position", !this.menuSelected ? "menu" : "numpad");
			this.menuSelected = !this.menuSelected;
		},
		toggleSearch() {
			setTimeout(() => {
				this.showSearch = !this.showSearch;
				setTimeout(() => {
					if(this.$refs["search-input-modal"]) {
						this.$refs["search-input-modal"].focus();
					}
				}, 350);
			}, 100);
		},
		closeSearch() {
			if(this.showSearch) {
				this.showSearch = false;
			}
		},
		getSavedMenuIndex() {
			const lastMenuId = localStorage.getItem("last_menu_id");
			if (lastMenuId) {
				const index = this.sortedMenus.findIndex(m => Number(m.menu_id) === Number(lastMenuId));
				if (index !== -1) {
					return index;
				}
			}
			return null;
		},
		fetchWarehouse() {
			if(!this.warehouseStore?.warehouseProducts?.length) {
				this.warehouseStore.getWarehouse();
			}
		},
		performShortcut() {
			if(!this.counterStore.editOrder) {
				if (!this.$refs.counterOrder) {
					console.warn('Counter order component ref not available');
					return;
				}
				if(this.userStore.currBusiness.counter_shortcut === "cash") {
					this.$refs.counterOrder.clickCta("cash");
				} else if(this.userStore.currBusiness.counter_shortcut === "card") {
					this.counterStore.paymentActive = true;
					this.$nextTick(() => {
						if (this.$refs.counterOrder?.$refs?.["bottom-payment"]) {
							this.$refs.counterOrder.$refs["bottom-payment"].startPayment('pos-fast');
						}
					});
				} else if(this.userStore.currBusiness.counter_shortcut === "paylater") {
					// TODO: Implement paylater shortcut action
					console.warn('Paylater shortcut not yet implemented');
				}
			}
		},
	},
	mounted() {
		this.menuIndex = this.getSavedMenuIndex();
		if ((this.sortedMenus.length > 0 && this.menuIndex === null) || (this.menuIndex !== null && !this.sortedMenus?.[this.menuIndex])) {
			this.menuIndex = 0;
		}
		this.noCategoriesDelayed = this.noCategories;
		this.fetchWarehouse();
	},
	watch: {
		sortedMenus(newVal) {
			this.menuIndex = this.getSavedMenuIndex();
			if ((newVal.length > 0 && this.menuIndex === null) || (this.menuIndex !== null && !this.sortedMenus?.[this.menuIndex])) {
				this.menuIndex = 0;
			}
		},
		"counterStore.totalPreSubtotal"(newVal, oldVal) {
			if(newVal !== oldVal) {
				this.counterStore.subtotalCosts = 0;
			}
		},
		"userStore.currBusinessId"() {
			this.menuIndex = null;

			if(this.userStore.currBusiness && !this.userStore.currBusiness.show_counter_numpad) {
				localStorage.removeItem("counter-sales-switch-position");
				this.menuSelected = true;
			}
			this.fetchWarehouse();
		},
		noCategories(newVal, oldVal) {
			if(newVal !== oldVal) {
				if(newVal) {
					this.noCategoriesDelayed = this.noCategories;
				} else {
					setTimeout(() => {
						this.noCategoriesDelayed = this.noCategories;
					}, 210);
				}
			}
		},
	},

}
</script>

<style lang="scss" scoped>
.counter-container {
	--orders-padding: 12px;

	display: flex;
	flex-direction: row;
	height: 100%;


	.counter-menu-container {
		width: calc(100% - 370px);
		overflow: hidden;
		position: relative;
		display: flex;
		flex-direction: column;
		@include hideScrollbar;

		.floating-buttons-container {
			display: none;
			position: fixed;
			bottom: 20px;
			right: 20px;
			z-index: 7;
			flex-direction: row;
			gap: 10px;
			align-items: center;
			
			@media (max-width: 750px) {
				display: flex;
			}
		}

		.floating-button {
			display: flex;
			background-color: var(--color-primary);
			box-shadow: 0 3px 6px var(--color-box-shadow-3), inset 0 3px 6px var(--color-box-shadow-2);
			border-radius: 20px;
			flex-direction: row;
			justify-content: center;
			align-items: center;
			cursor: pointer;
			border: 4px solid var(--color-bg);
			transition: background-color 0.2s ease-in-out;
			width: 80px;
			height: 60px;
			user-select: none;

			p {
				color: var(--color-txt-white);
				font-size: 22px;
				font-weight: 700;
				margin: 0;
			}

			&.shortcut {
				&.disabled {
					opacity: 1 !important;
					pointer-events: none;
					background-color: var(--color-bg-primary-2);
					img {
						opacity: 1;
					}
				}
			}


			img {
				width: 20px;
				height: 20px;
			}
		}

		.macro-filters-container {
			background: rgba(var(--rgb-bg-primary-1), 67%);
			box-shadow: inset -3px -3px 6px var(--color-box-shadow-2);
			border-radius: 0 0 13px 0;
			opacity: 1;
			backdrop-filter: blur(6px);
			-webkit-backdrop-filter: blur(6px);
			z-index: 5;
			padding: var(--orders-padding);
			flex-shrink: 0;

			@media (max-width: 750px) {
				border-radius: 0 0 13px 13px;
			}

			.first-row {
				display: flex;
				flex-direction: row;
				justify-content: space-between;
				gap: 12px;
				height: 66px;

				.select-wrapper {
					max-width: 200px;

					@media (max-width: 1050px) {
						max-width: 140px;
					}

					@media (max-width: 900px) {
						max-width: 130px;
					}

					@media (max-width: 420px) {
						max-width: 110px;
						min-width: 110px;
					}

					&:deep(.select-title) {

						@media (max-width: 900px) {
							height: 27px;
						}

					}
				}

				.left-search-container {
					display: flex;
					flex-direction: row;
					align-items: center;
					justify-content: flex-end;

					.search-container {
						display: flex;
						flex-direction: row;
						justify-content: flex-end;
						align-items: center;

						.input-wrapper {
							width: 200px;
							border: 1px solid rgba(0,0,0,0);
							border-radius: 13px;
							transition: border 0.2s ease-in-out;

							&.searching {
								border: 1px solid var(--color-primary);
							}

							:deep(.input-element) {
								input {
									height: 48px;
									padding-left: 32px;
								}
							}

							@media (max-width: 1050px) {
								width: 140px;
							}

							@media (max-width: 900px) {
								width: 130px;

								:deep(.input-element) {

									img {
										width: 14px;
										height: 14px;
									}

									input {
										height: 27px;
										padding-left: 32px;
									}
								}
							}

							@media (max-width: 420px) {
								width: 120px;
							}

						}

						.search-icon {
							margin-right: 8px;
						}
					}

					:deep(.switch) {

						.switch-container {
							@media (max-width: 900px) {
								width: 68px;
								height: 27px;

								.switch-slider {
									&:before, &:after {
										height: 22px;
										width: 33px;
										left: 3px;
									}
								}

								input:checked + .switch-slider:before,
								input:checked + .switch-slider:after {
									left: calc(100% - 36px);
								}

								.row .col-6 svg {
									height: calc(100% - 17px);
								}
							}
						}

					}
				}

			}

			.second-row {
				display: flex;
				flex-direction: row;
				align-items: center;
				overflow-x: auto;
				@include hideScrollbar;
				border-radius: 0 13px 13px 13px;
				background-color: var(--color-bg-primary-2);
				box-shadow: inset 2px 2px 3px var(--color-box-shadow-3);
				min-height: 56px;

				&.rounded {
					border-radius: 13px;
				}
			}
		}

		.categories-container {
			padding: 0 var(--orders-padding);
			position: relative;
			flex: 1 1 auto;
			min-height: 0;
			display: flex;
			flex-direction: column;

			.all-categories-container {
				opacity: 0;
				transition: opacity 0.2s ease 0.2s;
				flex: 1 1 auto;
				min-height: 0;
				overflow-y: auto;
				overflow-x: hidden;
				padding-top: var(--orders-padding);
				padding-bottom: 90px;
				@include hideScrollbar;

				&.appearing {
					opacity: 1;
				}
			}

			.category-items-row {
				display: flex;
				flex-direction: row;
				flex-wrap: wrap;
				--items-gap: 10px;
				gap: var(--items-gap);
				padding-bottom: 10px;

				@media (max-width: 700px) {
					--items-gap: 8px;
					padding-bottom: 8px;
				}

				&.is-virtual {
					:deep(.category-item) {
						color: var(--color-primary);
						background-color: var(--color-alpha-1);
						&.deletable {
							box-shadow: 0 1px 3px 1px var(--color-box-shadow-3) inset;
							background-color: var(--color-alpha-4);
						}
					}
				}
			}

			.no-categories-container {
				width: calc(100% - (var(--orders-padding) * 2));
				opacity: 0;
				transition: opacity 0.2s ease;
				position: absolute;
				top: var(--orders-padding);
				left: var(--orders-padding);

				&.appearing {
					opacity: 1;
				}

				.no-categories {
					width: 100%;
					background-color: var(--color-bg);
					border-radius: 13px;
					border: 2px dashed $primary;
					padding: 20px;
					display: flex;
					align-items: center;
					justify-content: center;
					flex-direction: column;
					min-height: 130px;

					.no-categories-text {
						font-size: 18px;
						color: $primary;
						font-weight: 700;
					}
				}
			}
		}

	}
	
	.counter-order-container {
		width: 370px;
		overflow-y: auto;
		padding: var(--orders-padding);
		@include hideScrollbar;
		position: relative;

		&:before {
			content: "";
			display: block;
			width: 100%;
			height: 100%;
			position: absolute;
			top: 0;
			left: 0;
			background: black;
			opacity: 0;
			transition: opacity 0.5s ease;
		}
	}


	@media (max-width: 1080px) {
		--orders-padding: 10px;

		.counter-menu-container {
			width: calc(100% - 330px);
		}

		.counter-order-container {
			width: 330px;
		}
	}

	@media (max-width: 750px) {

		.counter-menu-container {
			width: 100%;
		}

		.counter-order-container {
			width: 100%;
			height: 100%;
			align-items: center;
			position: absolute;
			top: 0;
			z-index: 8;
			display: none;
			justify-content: flex-end;

			.counter-order {
				width: 100%;
				max-width: 400px;
				margin-right: -100%;

			}

			&.transition {
				display: flex;

				&:before {
					opacity: 0;
				}
			}

			&.active {

				&:before {
					opacity: 0.67;
				}

				.counter-order {
					margin-right: 0;
				}
			}

		}
	}

	@media (max-width: 750px) {
		--orders-padding: 8px;
	}


	@media (max-height: 500px) and (max-width: 750px) {
		.counter-order-container {
			height: unset;
		}
	}

	@media (max-width: 480px) {
		.counter-order-container {
			padding-left: 44px;
		}
	}
}
.with-steps-sheet .counter-container .counter-menu-container .floating-buttons-container {
	bottom: 70px;
}
.composite-loader {
	margin: 0 auto;
}
.composite-container {
	background-color: $alpha_1;
	padding: 10px;
	border-radius: 13px;
}
.order-switch {
	border-radius: 13px;
	border: 1px solid $primary;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 9px;
	transition: background-color 0.25s ease-in-out;
	cursor: pointer;
	width: 48px;
	height: 48px;

	img {
		width: 20px;
		height: 20px;
	}

	@media (max-width: 350px) {
		width: 38px;
		height: 38px;
		padding: 6px;
	}

	&.order-switch-search {
		box-shadow: 0 1px 2px var(--color-alpha-4);
		background-color: var(--color-bg);
		border: 1px solid var(--color-bg);
		transition: border 0.25s ease-in-out, background-color 0.25s ease-in-out;
		position: relative;

		&.selected {
			background-color: var(--color-alpha-1);
			border: 1px solid var(--color-primary);
		}

		&.searching {
			border: 1px solid var(--color-primary);
			background-color: var(--color-active-button);
		}

	}

}
.menus-empty-container {
	min-height: 48px;
	border-radius: 13px 13px 0 0;
	background-color: var(--color-bg);
	box-shadow: inset 2px 2px 3px var(--color-box-shadow-3);
	flex-grow: 1;

	&.noHeight {
		min-height: 0;
		height: 0;
	}
}
.menus-scroll-container {
	display: flex;
	flex-direction: row;
	align-items: center;
	overflow-x: auto;
	-ms-overflow-style: none;
	scrollbar-width: none;
	border-radius: 13px 13px 0 0;
	background-color: var(--color-bg);
	box-shadow: inset 2px 2px 3px var(--color-box-shadow-3);
	flex-grow: 1;
	height: 66px;

	.menus-filter-container {
		border-radius: 13px;
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		align-items: center;
		padding: 6px 8px;
		gap: 6px;


		.menu {
			border-radius: 13px;
			border: 1px solid var(--color-primary);
			padding: 0 12px;
			max-width: 130px;
			@include truncateString(1);
			font-size: 13px;
			color: $primary;
			width: fit-content;
			cursor: pointer;
			user-select: none;
			transition: background-color 0.3s ease;
			height: 40px;
			display: flex;
			align-items: center;

			&.active {
				background-color: var(--color-active-button);
				p {
					color: var(--color-active-button-text);
				}
			}
		}

		@media (max-width: 900px) {
			padding: 8px;
			gap: 8px;
			min-height: 46px;

			.menu {
				padding: 6px 8px;
			}
		}

	}
}
.wrapper-fixed-button {
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	height: 80px;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: rgba(var(--color-bg-primary-1), 65%);
	padding: 10px 20px 20px;
	backdrop-filter: blur(5px);
	-webkit-backdrop-filter: blur(5px);
	:deep(.base-button) {
		width: 100%;
		height: 50px;
		max-width: unset;
	}
}

.dark .counter-container .counter-menu-container .floating-button.shortcut.disabled img {
	opacity: 0.2;
}
</style>